import React, { ReactNode, useMemo } from 'react';
import { AppShellTemplate } from '@experiences/templates/app-shell-template';
import { GoToPmcSidebarButton } from '@finance-hub/components/go-to-pmc-sidebar-button';
import { ShellChromeProvider } from '@experiences/templates/shell-chrome-context';
import { useFinanceHubSidebarNav } from '@finance-hub/blocs/hooks/use-finance-hub-sidebar-nav';
import { useSidebarToggleLeadingAction } from '@experiences/templates/use-sidebar-toggle-leading-action';

interface FinanceHubShellTemplateProps {
  children: ReactNode;
}

/**
 * {@link AppShellTemplate} bound to the Finance Hub. Publishes the sidebar
 * toggle to {@link ShellChromeProvider} so screens' `AppPageLayout` picks it up
 * without each wiring the toggle itself.
 *
 * Screens own their page chrome via `AppPageLayout`, so this shell adds no top
 * bar of its own — see the `BaseSidebarTemplate` docstring.
 */
export const FinanceHubShellTemplate: React.FC<
  FinanceHubShellTemplateProps
> = ({ children }) => {
  const { loading, navItems } = useFinanceHubSidebarNav();
  const leadingAction = useSidebarToggleLeadingAction();
  const chrome = useMemo(() => ({ leadingAction }), [leadingAction]);

  return (
    <AppShellTemplate
      navItems={navItems}
      navItemsLoading={loading}
      sidebarFooter={<GoToPmcSidebarButton />}
    >
      <ShellChromeProvider value={chrome}>{children}</ShellChromeProvider>
    </AppShellTemplate>
  );
};
